import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase, type User } from '../services/supabase';

type PostCounts = { count: number }[];

export interface CommunityPost {
  id: string;
  title?: string | null;
  content: string;
  created_at: string;
  author_id: string;
  author_name?: string;
  status?: string | null;
  is_flagged?: boolean | null;
  images?: string[] | null;
  video_link?: string | null;
  video_url?: string | null;
  type?: string | null;
  is_pinned?: boolean | null;
  pinned_at?: string | null;
  pinned_by?: string | null;
  edited_at?: string | null;
  original_post?: Record<string, unknown> | null;
  poster_id?: string | null;
  tags?: string[] | string | null;
  external_link?: string | null;
  video_id?: string | null;
  wall_comments?: PostCounts;
  post_likes?: PostCounts;
  likesCount?: number;
  commentsCount?: number;
  seenCount?: number | null;
  viewerHasSeen?: boolean;
  /** Present when the post was prepended from the separate pinned fetch. */
  __fromPinnedSection?: boolean;
}

export interface CommunitySeenUser {
  userId: string;
  fullName: string;
  avatar?: string | null;
  lastSeenAt: string;
}

export interface CommunityComment {
  id: string;
  post_id: string;
  content: string;
  created_at: string;
  author_id: string;
  author_name?: string;
  reply_to_id?: string | null;
  status?: string | null;
  is_pinned?: boolean | null;
  pinned_at?: string | null;
  pinned_by?: string | null;
}

export interface CommunityPostInput {
  content: string;
  type?: string | null;
  status?: string;
  imageUrl?: string | null;
  videoUrl?: string | null;
  videoId?: string | null;
  scheduledFor?: string | null;
}

export interface CommunityCommentInput {
  postId: string;
  content: string;
  replyToId?: string | null;
}

export interface CommunityEngagementState {
  likedPostIds: string[];
  commentedPostIds: string[];
}

export interface CommunityFeedResult {
  posts: CommunityPost[];
  totalCount: number;
  page: number;
  pageSize: number;
}

type FeedOptions = {
  limit?: number;
  page?: number;
  status?: string;
  types?: string[];
  mediaOnly?: boolean;
};

const DEFAULT_FEED_TYPES = ['post', 'prayer', 'testimony', 'praise_report'];
const DEFAULT_FEED_LIMIT = 24;
const MEDIA_BUCKET = 'post-images';

export const communityQueryKeys = {
  root: ['community'] as const,
  feed: (options?: FeedOptions) =>
    [
      'community',
      'feed',
      options?.page ?? 1,
      options?.limit ?? DEFAULT_FEED_LIMIT,
      options?.status ?? 'published',
      (options?.types ?? DEFAULT_FEED_TYPES).join(','),
      options?.mediaOnly ? 'media-only' : 'all-media',
    ] as const,
  pinned: (options?: Pick<FeedOptions, 'status' | 'types' | 'mediaOnly'>) =>
    [
      'community',
      'pinned',
      options?.status ?? 'published',
      (options?.types ?? DEFAULT_FEED_TYPES).join(','),
      options?.mediaOnly ? 'media-only' : 'all-media',
    ] as const,
  post: (postId?: string) => ['community', 'post', postId ?? 'unknown'] as const,
  comments: (postId?: string) => ['community', 'comments', postId ?? 'unknown'] as const,
  engagement: (userId?: string, postIds?: string[]) =>
    ['community', 'engagement', userId ?? 'anonymous', (postIds ?? []).join(',')] as const,
  seenList: (postId?: string) => ['community', 'seen-list', postId ?? 'unknown'] as const,
};

const unique = (values: Array<string | null | undefined>) =>
  Array.from(new Set(values.filter((value): value is string => Boolean(value))));

async function resolveUserNames(userIds: string[]) {
  if (userIds.length === 0) return {} as Record<string, string>;

  const { data, error } = await supabase.from('users').select('id, full_name').in('id', userIds);
  if (error) throw error;

  const names: Record<string, string> = {};
  (data ?? []).forEach((row) => {
    names[row.id as string] = (row.full_name as string) || '';
  });
  return names;
}

async function enrichPosts(posts: CommunityPost[] | null | undefined): Promise<CommunityPost[]> {
  const rows = posts ?? [];
  const userNames = await resolveUserNames(unique(rows.map((post) => post.author_id)));
  return rows.map((post) => ({
    ...post,
    author_name: post.author_name || userNames[post.author_id] || post.author_id,
    likesCount:
      typeof post.likesCount === 'number' ? post.likesCount : Number(post.post_likes?.[0]?.count ?? 0),
    commentsCount:
      typeof post.commentsCount === 'number' ? post.commentsCount : Number(post.wall_comments?.[0]?.count ?? 0),
  }));
}

async function fetchCommunityPostSeenSummaries(postIds: string[]) {
  const uniquePostIds = unique(postIds);
  if (uniquePostIds.length === 0) {
    return {} as Record<string, { seenCount: number | null; viewerHasSeen: boolean }>;
  }

  const { data, error } = await supabase.rpc('get_wall_post_seen_summaries', {
    p_post_ids: uniquePostIds,
  });

  if (error) throw error;

  return Object.fromEntries(
    ((data ?? []) as Array<Record<string, unknown>>).map((row) => [
      String(row.post_id),
      {
        seenCount:
          row.seen_count === null || typeof row.seen_count === 'undefined'
            ? null
            : Number.isFinite(Number(row.seen_count))
              ? Number(row.seen_count)
              : null,
        viewerHasSeen: Boolean(row.viewer_has_seen),
      },
    ])
  );
}

async function fetchCommunityPostSeenList(postId: string) {
  if (!postId) return [] as CommunitySeenUser[];

  const { data, error } = await supabase.rpc('get_wall_post_seen_list', {
    p_post_id: postId,
  });

  if (error) throw error;

  return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
    userId: String(row.user_id),
    fullName:
      typeof row.full_name === 'string' && row.full_name.trim().length > 0 ? row.full_name : 'Community Member',
    avatar: typeof row.avatar === 'string' ? row.avatar : null,
    lastSeenAt: String(row.last_seen_at),
  }));
}

async function recordCommunityPostsSeen(postIds: string[]) {
  const uniquePostIds = unique(postIds);
  if (uniquePostIds.length === 0) return 0;

  const { data, error } = await supabase.rpc('mark_wall_posts_seen', {
    p_post_ids: uniquePostIds,
  });

  if (error) throw error;
  return typeof data === 'number' ? data : Number(data ?? 0);
}

async function enrichComments(
  comments: CommunityComment[] | null | undefined
): Promise<CommunityComment[]> {
  const rows = comments ?? [];
  const userNames = await resolveUserNames(unique(rows.map((comment) => comment.author_id)));
  return rows
    .map((comment) => ({
      ...comment,
      author_name: comment.author_name || userNames[comment.author_id] || comment.author_id,
      is_pinned: Boolean(comment.is_pinned),
      pinned_at: comment.pinned_at ?? null,
      pinned_by: comment.pinned_by ?? null,
    }))
    .sort((a, b) => {
      if (Boolean(a.is_pinned) !== Boolean(b.is_pinned)) {
        return a.is_pinned ? -1 : 1;
      }
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });
}

async function fetchPinnedCommunityPosts(options?: FeedOptions) {
  const status = options?.status ?? 'published';
  const types = options?.types ?? DEFAULT_FEED_TYPES;
  const mediaOnly = options?.mediaOnly ?? false;

  let query = supabase
    .from('wall_posts')
    .select('*, wall_comments(count), post_likes(count)')
    .eq('is_pinned', true)
    .order('pinned_at', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false })
    .limit(20);

  if (status) {
    query = query.or(`status.is.null,status.eq.${status},status.eq.approved`);
  }
  if (types.length > 0) {
    const escapedTypes = types.map((type) => `"${type}"`).join(',');
    query = query.or(`type.is.null,type.in.(${escapedTypes})`);
  }
  if (mediaOnly) {
    query = query.or('images.not.is.null,video_link.not.is.null,video_url.not.is.null');
  }

  const { data, error } = await query;
  if (error) throw error;
  const enriched = await enrichPosts((data ?? []) as CommunityPost[]);
  return enriched.map((post) => ({
    ...post,
    is_pinned: true,
    pinned_at: post.pinned_at ?? null,
    pinned_by: post.pinned_by ?? null,
    __fromPinnedSection: true,
  }));
}

async function fetchCommunityFeed(options?: FeedOptions) {
  const limit = options?.limit ?? DEFAULT_FEED_LIMIT;
  const page = Math.max(1, options?.page ?? 1);
  const status = options?.status ?? 'published';
  const types = options?.types ?? DEFAULT_FEED_TYPES;
  const mediaOnly = options?.mediaOnly ?? false;
  const from = (page - 1) * limit;
  const to = from + limit - 1;

  let query = supabase
    .from('wall_posts')
    .select('*, wall_comments(count), post_likes(count)', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(from, to);

  if (status) {
    query = query.eq('status', status);
  }
  if (types.length > 0) {
    const escapedTypes = types.map((type) => `"${type}"`).join(',');
    query = query.or(`type.is.null,type.in.(${escapedTypes})`);
  }
  if (mediaOnly) {
    query = query.or('images.not.is.null,video_link.not.is.null,video_url.not.is.null');
  }

  const { data, error, count } = await query;
  if (error) throw error;
  let enriched = await enrichPosts((data ?? []) as CommunityPost[]);
  enriched = enriched.map((post) => ({
    ...post,
    is_pinned: Boolean(post.is_pinned),
    pinned_at: post.pinned_at ?? null,
    pinned_by: post.pinned_by ?? null,
  }));

  // First page only: prepend separately-fetched pins so pagination cursors stay chronological.
  if (page === 1) {
    try {
      const pinned = await fetchPinnedCommunityPosts(options);
      if (pinned.length > 0) {
        const pinnedIds = new Set(pinned.map((post) => post.id));
        const chrono = enriched.filter((post) => !pinnedIds.has(post.id));
        enriched = [...pinned, ...chrono];
      }
    } catch {
      // Keep chronological feed if pinned fetch fails.
    }
  }

  const seenSummaryByPostId = await fetchCommunityPostSeenSummaries(enriched.map((post) => post.id));
  return {
    posts: enriched.map((post) => ({
      ...post,
      seenCount: seenSummaryByPostId[post.id]?.seenCount ?? null,
      viewerHasSeen: seenSummaryByPostId[post.id]?.viewerHasSeen ?? false,
    })),
    totalCount: count ?? enriched.length,
    page,
    pageSize: limit,
  } satisfies CommunityFeedResult;
}

async function fetchCommunityPost(postId: string) {
  const { data, error } = await supabase
    .from('wall_posts')
    .select('*, wall_comments(count), post_likes(count)')
    .eq('id', postId)
    .single();

  if (error) throw error;
  const posts = await enrichPosts(data ? ([data] as CommunityPost[]) : []);
  const post = posts[0] ?? null;
  if (!post?.id) return post;
  const seenSummaryByPostId = await fetchCommunityPostSeenSummaries([post.id]);
  return {
    ...post,
    seenCount: seenSummaryByPostId[post.id]?.seenCount ?? null,
    viewerHasSeen: seenSummaryByPostId[post.id]?.viewerHasSeen ?? false,
  } satisfies CommunityPost;
}

async function fetchCommunityComments(postId: string) {
  const { data, error } = await supabase
    .from('wall_comments')
    .select(
      'id, post_id, content, created_at, author_id, author_name, reply_to_id, status, is_pinned, pinned_at, pinned_by'
    )
    .eq('post_id', postId)
    .or('status.is.null,status.eq.published')
    .order('is_pinned', { ascending: false })
    .order('created_at', { ascending: true });

  if (error) throw error;
  return enrichComments((data ?? []) as CommunityComment[]);
}

async function setWallPostPinned(postId: string, pinned: boolean) {
  const { data, error } = await supabase.rpc('set_wall_post_pinned', {
    p_post_id: postId,
    p_pinned: pinned,
  });
  if (error) throw error;
  return {
    id: String((data as { id?: string } | null)?.id ?? postId),
    is_pinned: Boolean((data as { is_pinned?: boolean } | null)?.is_pinned ?? pinned),
  };
}

async function setWallCommentPinned(commentId: string, pinned: boolean) {
  const { data, error } = await supabase.rpc('set_wall_comment_pinned', {
    p_comment_id: commentId,
    p_pinned: pinned,
  });
  if (error) throw error;
  return {
    id: String((data as { id?: string } | null)?.id ?? commentId),
    post_id: String((data as { post_id?: string } | null)?.post_id ?? ''),
    is_pinned: Boolean((data as { is_pinned?: boolean } | null)?.is_pinned ?? pinned),
  };
}

async function fetchCommunityEngagement(userId: string, postIds: string[]) {
  if (!userId || postIds.length === 0) {
    return { likedPostIds: [], commentedPostIds: [] } satisfies CommunityEngagementState;
  }

  const [likesRes, commentsRes] = await Promise.all([
    supabase.from('post_likes').select('post_id').eq('user_id', userId).in('post_id', postIds),
    supabase
      .from('wall_comments')
      .select('post_id')
      .eq('author_id', userId)
      .in('post_id', postIds)
      .or('status.is.null,status.eq.published'),
  ]);

  if (likesRes.error) throw likesRes.error;
  if (commentsRes.error) throw commentsRes.error;

  return {
    likedPostIds: unique((likesRes.data ?? []).map((row) => row.post_id as string)),
    commentedPostIds: unique((commentsRes.data ?? []).map((row) => row.post_id as string)),
  } satisfies CommunityEngagementState;
}

function resolveAuthorName(user: User | null, fallback?: string | null) {
  if (fallback?.trim()) return fallback.trim();

  const userMeta = user?.user_metadata as Record<string, unknown> | undefined;
  const candidates = [
    userMeta?.full_name,
    userMeta?.name,
    userMeta?.display_name,
    user?.email,
    'Community Member',
  ];

  return (
    candidates.find((value): value is string => typeof value === 'string' && value.trim().length > 0) ??
    'Community Member'
  );
}

async function uploadCommunityMedia(file: File) {
  const extension = file.name.split('.').pop() || 'bin';
  const fileName = `posts/${Date.now()}_${Math.random().toString(36).slice(2)}.${extension}`;
  const { error } = await supabase.storage.from(MEDIA_BUCKET).upload(fileName, file, { upsert: false });
  if (error) throw error;

  const { data } = supabase.storage.from(MEDIA_BUCKET).getPublicUrl(fileName);
  return data.publicUrl;
}

export function useCommunityFeed(options?: FeedOptions) {
  return useQuery<CommunityFeedResult>({
    queryKey: communityQueryKeys.feed(options),
    queryFn: () => fetchCommunityFeed(options),
    staleTime: 60_000,
    refetchInterval: 180_000,
    refetchIntervalInBackground: true,
  });
}

export function useCommunityPost(postId?: string) {
  return useQuery<CommunityPost | null>({
    queryKey: communityQueryKeys.post(postId),
    queryFn: () => fetchCommunityPost(postId ?? ''),
    enabled: Boolean(postId),
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
  });
}

export function useCommunityComments(postId?: string) {
  return useQuery<CommunityComment[]>({
    queryKey: communityQueryKeys.comments(postId),
    queryFn: () => fetchCommunityComments(postId ?? ''),
    enabled: Boolean(postId),
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
  });
}

export function useCommunityEngagement(userId?: string, postIds?: string[]) {
  return useQuery<CommunityEngagementState>({
    queryKey: communityQueryKeys.engagement(userId, postIds),
    queryFn: () => fetchCommunityEngagement(userId ?? '', postIds ?? []),
    enabled: Boolean(userId) && Boolean(postIds?.length),
    initialData: { likedPostIds: [], commentedPostIds: [] },
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
  });
}

export function useCommunityPostSeenList(postId?: string, enabled: boolean = true) {
  return useQuery<CommunitySeenUser[]>({
    queryKey: communityQueryKeys.seenList(postId),
    queryFn: () => fetchCommunityPostSeenList(postId ?? ''),
    enabled: enabled && Boolean(postId),
    staleTime: 30_000,
  });
}

export function useRecordCommunityPostsSeen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (postIds: string[]) => recordCommunityPostsSeen(postIds),
    onSuccess: async (_updatedCount, postIds) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: communityQueryKeys.root }),
        ...unique(postIds).map((postId) =>
          queryClient.invalidateQueries({ queryKey: communityQueryKeys.seenList(postId) })
        ),
      ]);
    },
  });
}

export function useCommunityRealtime(options?: { enabled?: boolean; postId?: string }) {
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;
  const postId = options?.postId;

  useEffect(() => {
    if (!enabled) return undefined;

    const invalidateCommunity = () => {
      void queryClient.invalidateQueries({ queryKey: communityQueryKeys.root });
      if (postId) {
        void queryClient.invalidateQueries({ queryKey: communityQueryKeys.post(postId) });
        void queryClient.invalidateQueries({ queryKey: communityQueryKeys.comments(postId) });
      }
    };

    const channel = supabase
      .channel(`community-live-${postId ?? 'feed'}-${Date.now()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'wall_posts' }, invalidateCommunity)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'wall_comments',
          ...(postId ? { filter: `post_id=eq.${postId}` } : {}),
        },
        invalidateCommunity
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'post_likes',
          ...(postId ? { filter: `post_id=eq.${postId}` } : {}),
        },
        invalidateCommunity
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [enabled, postId, queryClient]);
}

export function useCreateCommunityPost(user: User | null, authorName?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CommunityPostInput) => {
      if (!user?.id) throw new Error('You must be signed in to post.');

      const trimmedContent = input.content.trim();
      if (!trimmedContent) throw new Error('Content is required to post.');

      const scheduledFor =
        input.scheduledFor && new Date(input.scheduledFor).getTime() > Date.now() ? input.scheduledFor : null;

      const payload: Record<string, unknown> = {
        author_id: user.id,
        author_name: resolveAuthorName(user, authorName),
        content: trimmedContent,
        status: scheduledFor ? 'scheduled' : (input.status ?? 'published'),
        type: input.type ?? 'post',
        scheduled_for: scheduledFor,
      };

      if (input.imageUrl?.trim()) payload.images = [input.imageUrl.trim()];
      if (input.videoUrl?.trim()) {
        // Prefer Bunny fields used by mobile; keep video_link for older web readers.
        payload.video_url = input.videoUrl.trim();
        payload.video_link = input.videoUrl.trim();
      }
      if (input.videoId?.trim()) {
        payload.video_id = input.videoId.trim();
        payload.video_processing_status = 'processing';
      }

      const { data, error } = await supabase.from('wall_posts').insert([payload]).select().single();
      if (error) throw error;
      return data as CommunityPost;
    },
    onSuccess: async (createdPost) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: communityQueryKeys.root }),
        queryClient.invalidateQueries({ queryKey: ['posts'] }),
      ]);
      queryClient.setQueryData(communityQueryKeys.post(createdPost.id), createdPost);
    },
  });
}

export function useEditCommunityPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      postId: string;
      content: string;
      images: string[];
      videoUrl?: string | null;
      videoId?: string | null;
      videoProcessingStatus?: string | null;
    }) => {
      const hasVideo = Boolean(input.videoUrl?.trim() || input.videoId?.trim());
      const { data, error } = await supabase.rpc('edit_wall_post', {
        p_post_id: input.postId,
        p_patch: {
          content: input.content,
          images: input.images,
          video_url: input.videoUrl?.trim() || null,
          video_id: input.videoId?.trim() || null,
          video_link: input.videoUrl?.trim() || null,
          video_processing_status: hasVideo ? (input.videoProcessingStatus || (input.videoId ? 'processing' : 'ready')) : null,
        },
      });
      if (error) throw error;
      return data as CommunityPost;
    },
    onSuccess: async (updatedPost) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: communityQueryKeys.root }),
        queryClient.invalidateQueries({ queryKey: ['posts'] }),
        updatedPost?.id
          ? queryClient.invalidateQueries({ queryKey: communityQueryKeys.post(updatedPost.id) })
          : Promise.resolve(),
      ]);
    },
  });
}

export function useToggleCommunityPostLike(userId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (postId: string) => {
      if (!userId) throw new Error('You must be signed in to like a post.');

      const { data: existingLike, error: existingError } = await supabase
        .from('post_likes')
        .select('post_id')
        .eq('post_id', postId)
        .eq('user_id', userId)
        .maybeSingle();

      if (existingError) throw existingError;

      if (existingLike) {
        const { error } = await supabase
          .from('post_likes')
          .delete()
          .eq('post_id', postId)
          .eq('user_id', userId);
        if (error) throw error;
        return { postId, liked: false };
      }

      const { error } = await supabase.from('post_likes').insert({
        post_id: postId,
        user_id: userId,
        created_at: new Date().toISOString(),
      });
      if (error) throw error;
      return { postId, liked: true };
    },
    onSuccess: async (_result, postId) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: communityQueryKeys.root }),
        queryClient.invalidateQueries({ queryKey: communityQueryKeys.post(postId) }),
      ]);
    },
  });
}

export function useCreateCommunityComment(user: User | null, authorName?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CommunityCommentInput) => {
      if (!user?.id) throw new Error('You must be signed in to comment.');

      const trimmedContent = input.content.trim();
      if (!trimmedContent) throw new Error('Comment content is required.');

      const payload = {
        post_id: input.postId,
        content: trimmedContent,
        author_id: user.id,
        author_name: resolveAuthorName(user, authorName),
        reply_to_id: input.replyToId ?? null,
        status: 'published',
        created_at: new Date().toISOString(),
      };

      const { data, error } = await supabase.from('wall_comments').insert(payload).select().single();
      if (error) throw error;
      return data as CommunityComment;
    },
    onSuccess: async (createdComment) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: communityQueryKeys.root }),
        queryClient.invalidateQueries({ queryKey: communityQueryKeys.comments(createdComment.post_id) }),
        queryClient.invalidateQueries({ queryKey: communityQueryKeys.post(createdComment.post_id) }),
      ]);
    },
  });
}

export function useCommunityMediaUpload() {
  return useMutation({
    mutationFn: uploadCommunityMedia,
  });
}

export function useSetCommunityPostPinned() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, pinned }: { postId: string; pinned: boolean }) =>
      setWallPostPinned(postId, pinned),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: communityQueryKeys.root }),
        queryClient.invalidateQueries({ queryKey: communityQueryKeys.post(result.id) }),
        queryClient.invalidateQueries({ queryKey: ['posts'] }),
      ]);
    },
  });
}

export function useSetCommunityCommentPinned() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId, pinned }: { commentId: string; pinned: boolean }) =>
      setWallCommentPinned(commentId, pinned),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: communityQueryKeys.root }),
        result.post_id
          ? queryClient.invalidateQueries({ queryKey: communityQueryKeys.comments(result.post_id) })
          : Promise.resolve(),
      ]);
    },
  });
}